import { useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { useMyOrganizationsQuery } from '@upright/ui/api/iam/organizations/hooks';
import { Location, useLocation, useNavigationType } from 'react-router-dom';
import { nextProfileBackTarget, ProfileBackTarget, readProfileBackTarget } from './company-profile-route';
import { profileNavigationOrganizationId, validateProfileOrganizationTrail } from './profile-organization-context';

/**
 * Each unified profile's Back, recorded per history entry as the user navigates (PLT-6869).
 *
 * Recorded here, from the navigation itself, rather than handed over in each link's state: a
 * profile is reached from dozens of link components (holdings tables, top-impact prose, screener
 * rows, chat answers), and a Back that depends on every one of them remembering to pass a parent
 * is missing wherever one forgot.
 *
 * Kept in session storage, keyed by react-router's history-entry key, so a reload, and browser
 * Back and Forward after one, return to entries whose Back is already known.
 */
const STORAGE_KEY = 'unified-shell.profile-back-trail';
const MAX_ENTRIES = 100;

let trail: Map<string, ProfileBackTarget | null> | undefined;
const listeners = new Set<() => void>();

function getTrail(): Map<string, ProfileBackTarget | null> {
  if (!trail) trail = new Map(loadTrail());
  return trail;
}

function loadTrail(): Array<[string, ProfileBackTarget | null]> {
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry): entry is [string, unknown] => Array.isArray(entry) && typeof entry[0] === 'string')
      .map(([key, target]): [string, ProfileBackTarget | null] => [key, readProfileBackTarget(target)]);
  } catch {
    return [];
  }
}

function recordBackTarget(key: string, target: ProfileBackTarget | null) {
  const entries = getTrail();
  // Re-inserted so the map's order is recency and the oldest entry is the one evicted.
  entries.delete(key);
  entries.set(key, target);
  for (const oldest of entries.keys()) {
    if (entries.size <= MAX_ENTRIES) break;
    entries.delete(oldest);
  }
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...entries]));
  } catch {
    // Storage full or blocked: Back still works for this tab until a reload.
  }
  listeners.forEach(listener => {
    listener();
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Mounted once, above every route. A layout effect so a profile that renders without a Back for
 * one frame on arrival is re-rendered with its recorded parent before the browser paints.
 */
export function ProfileBackTrailRecorder() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previous = useRef<Location | null>(null);
  useLayoutEffect(() => {
    const from = previous.current;
    previous.current = location;
    if (!from || from.key === location.key) return;
    const target = nextProfileBackTarget(from, location, navigationType, key => getTrail().get(key));
    if (target !== undefined) recordBackTarget(location.key, target);
  }, [location, navigationType]);
  return null;
}

/**
 * The current profile's Back: the page it was opened from, or `null` when this tab recorded none
 * (a direct link, a new tab, a bookmark), so the profile shows no Back rather than one to a Home
 * the reader never came from (PLT-7159).
 */
export function useProfileBackTarget(): ProfileBackTarget | null {
  const { target, organizationId, organizationIds } = useProfileNavigationContext();
  return target ? validateProfileOrganizationTrail(target, organizationId, organizationIds) : null;
}

/** The selected organisation for search and Home links, also on entity lenses without a query string. */
export function useNavigationOrganizationId(): number | undefined {
  return useProfileNavigationContext().organizationId;
}

function useProfileNavigationContext() {
  const { key, search } = useLocation();
  const target = useSyncExternalStore(subscribe, () => getTrail().get(key));
  const organizations = useMyOrganizationsQuery();
  // Validate membership, not investor-menu eligibility: Company organisations also have a shell.
  const organizationIds = organizations.data?.organizations.map(org => org.id) ?? [];
  return {
    target,
    organizationIds,
    organizationId: profileNavigationOrganizationId(search, target, organizationIds),
  };
}
